import { getTableIntentLink } from "@components/drillDown/DrillDown.utils";
import { IconSelectClean } from "@components/inputs/select/IconSelect";
import { ifAll, IGetValueArgs, TFieldDefFn, TGetValueFn } from "@components/smart/FieldInfo";
import { SymbolConstantDef, SymbolSpecificDef, SymbolVariableDef } from "@components/smart/GeneralFieldDefinition";
import { IColumn, TCellValue } from "@components/table";
import TableWithAutoSizedColumns from "@components/table/TableWithAutoSizedColumns";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    IOtherLiabilityEntity,
    IPrDeductionEntity,
    IPrDeductionTemporalEntity,
    IPrDepositedDeductionEntity,
    IPrDepositedDistrainmentAmountEntity,
    PrDeductionEntity,
    PrDepositedDeductionEntity,
    PrDepositedDistrainmentAmountEntity,
    PrPayslipEntity
} from "@odata/GeneratedEntityTypes";
import {
    ClearedStatusCode,
    PrChildSupportDeductionTypeCode,
    PrDeductionCalculationTypeCode,
    PrDeductionTypeCode,
    PrDistrainmentDepositClosingCode,
    PrInsolvencyDepositClosingCode
} from "@odata/GeneratedEnums";
import { getEnumSelectItems } from "@odata/GeneratedEnums.utils";
import {
    getBankAccountFieldsDef,
    getBankAccountsFieldBaseDef,
    SAVED_ACCOUNTS_PATH
} from "@pages/banks/bankAccounts/BankAccounts.utils";
import { IDeductionCustomData } from "@pages/payroll/deduction/DeductionFormView";
import { createDateFromPeriod, getPayrollCurrencyFormatter } from "@pages/payroll/payroll.utils";
import { formatPayslipPeriodTableCell } from "@pages/payroll/payslip/Payslip.utils";
import { forEachKey, isDefined } from "@utils/general";
import { memoizeOne } from "@utils/memoizeOne";
import Big from "big.js";
import i18next from "i18next";
import React from "react";

import { DASH_CHARACTER } from "../../../constants";
import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { TextAlign, ToolbarItemType } from "../../../enums";
import { Optional } from "../../../global.types";
import { TableStorage } from "../../../model/TableStorage";
import { ROUTE_DEDUCTIONS, ROUTE_OTHER_LIABILITY } from "../../../routes";
import { IFormDef } from "../../../views/formView/Form";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ICustomButtonDefArgs, TableActionOrder, TTableToolbarItem } from "../../../views/table/TableToolbar.utils";

export const DEDUCTION_ADD_BUTTON = "deductionAddButton";
export const ADD_EMPLOYEE_ITEM = "addEmployeeItem";
export const INSOLVENCY_DEPOSIT_CLOSING = "insolvencyDepositClosing";
export const ADD_OTHER_PAYMENT_ACTION = "addOtherPaymentAction";

export interface IDepositedDeduction extends Omit<IPrDepositedDeductionEntity, "InsolvencyDeposit">, Omit<IPrDepositedDistrainmentAmountEntity, "DistrainmentDeposit"> {
    Deposit: IPrDeductionEntity;
}

// extends with document prop - deducted amounts could be deducted to insolvency or by liability document
export interface IDeductedAmounts extends Optional<Omit<IDepositedDeduction, "Id">, "Deposit"> {
    Id: string;
    Document?: IOtherLiabilityEntity;
}

export const isAmongTypes = (types: PrDeductionTypeCode[]) => (args: IGetValueArgs): boolean => {
    return types.includes(args.storage.data.entity.TypeCode);
};

export const isDepositDeductionClosed = (args: IGetValueArgs): boolean => {
    const entity = args.storage.data.entity as IPrDeductionEntity;
    return !!entity?.InsolvencyDepositClosingCode || !!entity?.DistrainmentDepositClosingCode;
};

export const hasDepositClosingDocumentBeenLiquidated = (entity: IPrDeductionEntity): boolean => {
    const code = entity?.DepositClosingDocument?.ClearedStatusCode;
    return code === ClearedStatusCode.Cleared || code === ClearedStatusCode.PartiallyCleared;
};

export const isRegularChildSupport = (args: IGetValueArgs): boolean => {
    return args.storage.data.entity.TypeCode === PrDeductionTypeCode.ChildSupport && args.storage.data.entity.ChildSupportType?.Code === PrChildSupportDeductionTypeCode.Regular;
};

export const isDebtTypeOfChildSupport = (args: IGetValueArgs): boolean => {
    return args.storage.data.entity.TypeCode !== PrDeductionTypeCode.ChildSupport || args.storage.data.entity.ChildSupportType?.Code !== PrChildSupportDeductionTypeCode.Regular;
};

export const isDecreeRequiredForChildSupport = (args: IGetValueArgs): boolean => {
    const entity = args.storage.data.entity as IPrDeductionEntity;
    return entity.TypeCode === PrDeductionTypeCode.ChildSupport && (
        entity.ChildSupportType?.Code === PrChildSupportDeductionTypeCode.Assigned ||
        entity.ChildSupportType?.Code === PrChildSupportDeductionTypeCode.Substitute
    );
};

export const isCounterparty: TGetValueFn<boolean> = (args) => {
    const entity = args.storage.data.entity as IPrDeductionEntity;
    return entity.TypeCode !== PrDeductionTypeCode.Custom || !entity.IsCompanyOwn;
};

export const DEBT_BALANCE_PATH = BindingContext.localContext("DeductionBalance");
export const PAID_AMOUNT_PATH = BindingContext.localContext("PaidAmount");

type DeductionTypeCodeString = `Deduction${PrDeductionTypeCode}`

function getIconByDeductionTypeCode(typeCode: PrDeductionTypeCode): DeductionTypeCodeString {
    switch (typeCode) {
        case PrDeductionTypeCode.ChildSupport:
            return "DeductionChildSupport";
        case PrDeductionTypeCode.Custom:
            return "DeductionCustom";
        case PrDeductionTypeCode.Distrainment:
            return "DeductionDistrainment";
        case PrDeductionTypeCode.DistrainmentDeposit:
            return "DeductionDistrainmentDeposit";
        case PrDeductionTypeCode.Insolvency:
            return "DeductionInsolvency";
        case PrDeductionTypeCode.InsolvencyAdministratorReward:
            return "DeductionInsolvencyReward";
        case PrDeductionTypeCode.InsolvencyDeposit:
            return 'DeductionInsolvencyDeposit';
    }
}

export function getToolbarAddButtonConfig(storage: TableStorage, ref: React.RefObject<IconSelectClean>, args?: ICustomButtonDefArgs): TTableToolbarItem {
    const items = getEnumSelectItems(EntityTypeName.PrDeductionType).map(item => ({
        ...item,
        iconName: getIconByDeductionTypeCode(item.id as PrDeductionTypeCode),
    }));

    return {
        id: DEDUCTION_ADD_BUTTON,
        itemType: ToolbarItemType.IconSelect,
        iconName: "Add",
        label: i18next.t("Deduction:NewDeduction"),
        order: TableActionOrder.Add,
        itemProps: {
            isTransparent: true,
            selectProps: {
                isActive: storage.data.addingRow || args?.isActive,
                isDisabled: !!storage.tableAction || args?.isDisabled,
                ref
            },
            items
        }
    };
}

export function isPercentageAmountDeduction(args: IGetValueArgs): boolean {
    const { storage, bindingContext } = args;
    const deductionTemporalEntity = storage.getValue(bindingContext.getParent()) as IPrDeductionTemporalEntity;
    return deductionTemporalEntity.CalculationTypeCode !== PrDeductionCalculationTypeCode.Fixed
        && args.storage.data.entity.TypeCode !== PrDeductionTypeCode.ChildSupport && args.storage.data.entity.TypeCode !== PrDeductionTypeCode.InsolvencyAdministratorReward;
}

interface IBalanceCalculationResult {
    amountDebtTotal: number;
    paidAmount: number;
    balance: number;
}

export const calculateBalance = memoizeOne((storage: FormStorage): IBalanceCalculationResult => {
    const entity = storage.data.entity as IPrDeductionEntity;
    const origEntity = storage.data.origEntity as IPrDeductionEntity;
    let paidAmount = new Big(0), balance = new Big(0);
    const amountDebtTotal = entity.AmountDebtTotal;

    if (isDefined(amountDebtTotal) && !isNaN(amountDebtTotal)) {
        paidAmount =
            entity.PayslipDocuments?.reduce((acc, payment) => {
                return acc.add(payment.Document?.Amount ?? 0);
            }, new Big(0)) ?? new Big(0);

        origEntity.OtherPayments?.forEach(payment => {
            paidAmount = paidAmount.add(payment.Amount ?? 0);
        });

        const depositedAmount = origEntity.DepositedAmount ?? 0;

        balance = new Big(amountDebtTotal).sub(paidAmount).sub(depositedAmount);
    }

    return { amountDebtTotal, paidAmount: paidAmount.toNumber(), balance: balance.toNumber() };
}, (storage: FormStorage) => {
    const entity = storage.data.entity as IPrDeductionEntity;
    const origEntity = storage.data.origEntity as IPrDeductionEntity;
    return [entity.AmountDebtTotal, entity.PayslipDocuments, origEntity.OtherPayments];
});

export const isTransferDeposit: TGetValueFn<boolean> = (args) => {
    const entity = (args.storage as FormStorage<IPrDeductionEntity>).data.entity;
    return entity.TypeCode === PrDeductionTypeCode.DistrainmentDeposit
        ? entity.DistrainmentDepositClosingCode === PrDistrainmentDepositClosingCode.TransferToDistrainer
        : entity.InsolvencyDepositClosingCode === PrInsolvencyDepositClosingCode.TransferToInsolvencyAdministrator;
};

export function addBankAccountDefinitions(formDef: IFormDef): void {
    const bankAccountFields = getBankAccountsFieldBaseDef();
    const bankAccountDef = getBankAccountFieldsDef({ addPaymentMethod: false, areFieldsDisabled: false, type: null });

    const depositVisibility: TGetValueFn<boolean> = (args) => {
        const { entity } = (args.storage as FormStorage<IPrDeductionEntity>).data;
        const isDepositType = [PrDeductionTypeCode.DistrainmentDeposit, PrDeductionTypeCode.InsolvencyDeposit].includes(entity.TypeCode);
        return isDepositType ? isTransferDeposit(args) : true;
    };

    const isBankAccountVisible = ifAll(isCounterparty, depositVisibility);

    const fieldDefs: IFormDef["fieldDefinition"] = {
        [PrDeductionEntity.SymbolVariable]: { ...SymbolVariableDef, isVisible: isBankAccountVisible },
        [PrDeductionEntity.SymbolSpecific]: { ...SymbolSpecificDef, isVisible: isBankAccountVisible },
        [PrDeductionEntity.SymbolConstant]: { ...SymbolConstantDef, isVisible: isBankAccountVisible },
        [PrDeductionEntity.RemittanceInformation]: { isVisible: isBankAccountVisible },
        [SAVED_ACCOUNTS_PATH]: {
            ...bankAccountDef[SAVED_ACCOUNTS_PATH],
            isRequired: true,
            isVisible: isBankAccountVisible,
            collapsedRows: [
                [{ id: "BankAccount/Country" }],
                [
                    { id: "BankAccount/AccountNumber" },
                    { id: "BankAccount/Bank" },
                    { id: "BankAccount/IBAN" },
                    { id: "BankAccount/SWIFT" },
                ],
            ],
        },
    };

    forEachKey(bankAccountFields, (key) => {
        fieldDefs[createPath(PrDeductionEntity.BankAccount, key)] = {
            ...bankAccountFields[key],
            isRequired: true,
            isVisible: isBankAccountVisible,
            customizationData: {
                ...(bankAccountFields[key].customizationData ?? {}),
                useForCustomization: true
            }
        };
    });

    // Merge the new field definitions with the existing ones in the form definition
    formDef.fieldDefinition = {
        ...formDef.fieldDefinition,
        ...fieldDefs,
    };
}

export function getDateEndFromLastDeduction(depositedDeductions: IPrDepositedDeductionEntity[]): Date | null {
    if (!depositedDeductions?.length) {
        return null;
    }

    let lastDeduction: IPrDepositedDeductionEntity = null;
    depositedDeductions.forEach(d => {
        if (!lastDeduction || lastDeduction.Payslip.Year < d.Payslip.Year
            || (lastDeduction.Payslip.Year === d.Payslip.Year && lastDeduction.Payslip.Month < d.Payslip.Month)) {
            lastDeduction = d;
        }
    });

    return createDateFromPeriod(lastDeduction.Payslip, null, true);
}

export function formatDeductionTableCell(deduction: IPrDeductionEntity, context: IAppContext): TCellValue {
    if (!deduction?.NumberOurs) {
        return DASH_CHARACTER;
    }

    return getTableIntentLink(deduction.NumberOurs, {
        route: `${ROUTE_DEDUCTIONS}/${deduction.Id}`,
        context,
    });
}

export const renderDeductedCustomTab: TFieldDefFn = (args) => {
    const { deducted } = args.storage.getCustomData() as IDeductionCustomData;

    const columns: IColumn[] = [
        { id: "month", label: i18next.t("Deduction:Form.ForPeriod"), textAlign: TextAlign.Left },
        { id: "amount", label: i18next.t("Deduction:Form.DeductionAmount"), textAlign: TextAlign.Right },
        { id: "liability", label: i18next.t("Deduction:Form.SettledByDocument") },
        { id: "deposited", label: i18next.t("Deduction:Form.Deposited"), },
    ];

    const rows = deducted.map((item) => ({
        id: item.Id,
        values: {
            month: formatPayslipPeriodTableCell(item.Payslip, args.storage.context),
            amount: getPayrollCurrencyFormatter(undefined, true)(item.DepositedAmount, args),
            liability: item.Document?.NumberOurs
                ? getTableIntentLink(item.Document?.NumberOurs?.toString(), {
                    route: `${ROUTE_OTHER_LIABILITY}/${item.Document?.Id?.toString()}`,
                    context: args.storage.context,
                })
                : DASH_CHARACTER,
            deposited: formatDeductionTableCell(item.Deposit, args.storage.context)
        },
    }));
    return (
        <TableWithAutoSizedColumns
            rows={rows}
            columns={columns}
            disableSort={true}
            disableVirtualization={true}
        />
    );
};

export async function loadDepositedDeductions(storage: FormStorage): Promise<IPrDepositedDeductionEntity[]> {
    const { Id } = storage.data.entity;
    if (!Id) {
        return [];
    }
    const isInsolvencyDeposit = storage.data.entity.TypeCode === PrDeductionTypeCode.InsolvencyDeposit;
    const filterProp = isInsolvencyDeposit ? PrDepositedDeductionEntity.InsolvencyDeposit
        : PrDepositedDeductionEntity.DepositedDeduction;

    const query = storage.oData.getEntitySetWrapper(EntitySetName.PrDepositedDeductions).query()
        .select(PrDepositedDeductionEntity.DepositedAmount, PrDepositedDeductionEntity.Id)
        .filter(`${filterProp}/Id eq ${Id}`)
        .expand(PrDepositedDeductionEntity.Payslip, (q) =>
            q.select(PrPayslipEntity.Month, PrPayslipEntity.Year, PrPayslipEntity.Id))
        .expand(PrDepositedDeductionEntity.DepositedDeduction, (q) =>
            q.select(PrDeductionEntity.NumberOurs, PrDeductionEntity.Name, PrDeductionEntity.Id))
        .expand(PrDepositedDeductionEntity.InsolvencyDeposit, (q) =>
            q.select(PrDeductionEntity.NumberOurs, PrDeductionEntity.Name, PrDeductionEntity.Id));

    const result = await query.fetchData<IPrDepositedDeductionEntity[]>();
    return result.value;
}

export async function loadDepositedDistrainmentAmounts(storage: FormStorage): Promise<IDepositedDeduction[]> {
    const { Id } = storage.data.entity;
    if (!Id) {
        return [];
    }
    const query = storage.oData.getEntitySetWrapper(EntitySetName.PrDepositedDistrainmentAmounts).query()
        .select(PrDepositedDistrainmentAmountEntity.DepositedAmount, PrDepositedDistrainmentAmountEntity.Id)
        .filter(`${PrDepositedDistrainmentAmountEntity.DistrainmentDeposit}/Id eq ${Id}`)
        .expand(PrDepositedDistrainmentAmountEntity.Payslip, (q) =>
            q.select(PrPayslipEntity.Month, PrPayslipEntity.Year, PrPayslipEntity.Id));

    const result = await query.fetchData<IPrDepositedDistrainmentAmountEntity[]>();
    return result.value.map(d => ({ ...d, Deposit: d.DistrainmentDeposit }));
}